import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Building2,
  User,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileText,
  RefreshCw,
  Eye,
  Link as LinkIcon,
  AlertCircle,
} from "lucide-react";

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC / Air Conditioning" },
  { value: "structural", label: "Structural" },
  { value: "appliances", label: "Appliances" },
  { value: "roofing", label: "Roofing" },
  { value: "painting", label: "Painting" },
  { value: "flooring", label: "Flooring" },
  { value: "doors_windows", label: "Doors & Windows" },
  { value: "pest_control", label: "Pest Control" },
  { value: "general", label: "General Maintenance" },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-green-100 text-green-800" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
  { value: "emergency", label: "Emergency", color: "bg-red-100 text-red-800" },
];

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-gray-100 text-gray-800", icon: Clock },
  in_review: { label: "In Review", color: "bg-blue-100 text-blue-800", icon: Eye },
  awaiting_landlord: { label: "Awaiting Landlord", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
  landlord_approved: { label: "Landlord Approved", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  landlord_rejected: { label: "Landlord Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800", icon: Wrench },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500", icon: XCircle },
};

const MaintenanceManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("requests");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState({ status: "", priority: "" });

  // Roles that can manage maintenance requests
  const managerRoles = ["project_manager", "admin", "ceo", "accounts", "letting_manager"];
  const isProjectManager = managerRoles.includes(user?.role);
  const isTenant = user?.role === "agent" || (!isProjectManager && user?.role !== "constructor");

  // Form state for new request
  const [newRequest, setNewRequest] = useState({
    rental_id: "",
    category: "general",
    priority: "medium",
    title: "",
    description: "",
    photos: [],
    preferred_date: "",
    access_instructions: "",
  });

  // Review form state
  const [reviewForm, setReviewForm] = useState({
    estimated_cost: "",
    notes: "",
  });

  // Landlord response form
  const [landlordForm, setLandlordForm] = useState({
    response: "approved",
    notes: "",
  });

  // Complete form state
  const [completeForm, setCompleteForm] = useState({
    actual_cost: "",
    resolution_notes: "",
  });

  const fetchRequests = async () => {
    try {
      let url = `${API}/maintenance`;
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.priority) params.append("priority", filter.priority);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      setRequests(response.data.requests || []);
    } catch (error) {
      toast.error("Failed to load maintenance requests");
    }
  };

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/tenant`);
      setRentals(response.data.rentals || []);
    } catch (error) {
      // Non-critical - tenant may not have rentals
    }
  };

  const fetchStats = async () => {
    if (!isProjectManager) return;
    try {
      const response = await axios.get(`${API}/maintenance/stats`);
      setStats(response.data);
    } catch (error) {
      // Non-critical
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRequests(), fetchRentals(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [filter]);

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!newRequest.rental_id) {
      toast.error("Please select a property");
      return;
    }
    if (!newRequest.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    try {
      await axios.post(`${API}/maintenance`, newRequest);
      toast.success("Maintenance request submitted successfully");
      setShowCreateForm(false);
      setNewRequest({
        rental_id: "",
        category: "general",
        priority: "medium",
        title: "",
        description: "",
        photos: [],
        preferred_date: "",
        access_instructions: "",
      });
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    }
  };

  const handleReview = async (requestId) => {
    if (!reviewForm.estimated_cost || parseFloat(reviewForm.estimated_cost) <= 0) {
      toast.error("Please enter a valid estimated cost");
      return;
    }

    try {
      const response = await axios.put(`${API}/maintenance/${requestId}/review`, {
        estimated_cost: parseFloat(reviewForm.estimated_cost),
        notes: reviewForm.notes,
      });
      
      if (response.data.requires_landlord_auth) {
        toast.success("Request reviewed - Landlord authorization required");
      } else {
        toast.success("Request approved - Work can proceed");
      }
      
      setSelectedRequest(null);
      setReviewForm({ estimated_cost: "", notes: "" });
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review request");
    }
  };

  const handleLandlordResponse = async (requestId) => {
    try {
      await axios.put(`${API}/maintenance/${requestId}/landlord-response`, landlordForm);
      toast.success(`Landlord response recorded: ${landlordForm.response}`);
      setSelectedRequest(null);
      setLandlordForm({ response: "approved", notes: "" });
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record response");
    }
  };

  const handleLinkProject = async (requestId) => {
    try {
      const response = await axios.put(`${API}/maintenance/${requestId}/link-project`);
      toast.success("Project created and linked successfully");
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    }
  };

  const handleComplete = async (requestId) => {
    try {
      await axios.put(`${API}/maintenance/${requestId}/complete`, {
        actual_cost: completeForm.actual_cost ? parseFloat(completeForm.actual_cost) : null,
        resolution_notes: completeForm.resolution_notes,
      });
      toast.success("Maintenance request marked as completed");
      setSelectedRequest(null);
      setCompleteForm({ actual_cost: "", resolution_notes: "" });
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete request");
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm("Are you sure you want to cancel this request?")) return;
    
    try {
      await axios.delete(`${API}/maintenance/${requestId}`);
      toast.success("Request cancelled");
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Maintenance Requests</h1>
          <p className="text-muted-foreground">
            {isProjectManager ? "Manage property maintenance requests" : "Report and track maintenance issues"}
          </p>
        </div>
        {isTenant && rentals.length > 0 && (
          <Button onClick={() => setShowCreateForm(true)} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        )}
      </div>

      {/* Stats Cards (PM only) */}
      {isProjectManager && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.pending}</div>
              <p className="text-sm text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.awaiting_landlord}</div>
              <p className="text-sm text-muted-foreground">Awaiting Landlord</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.in_progress}</div>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-3 py-2 border rounded-md text-sm"
          data-testid="status-filter"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filter.priority}
          onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
          className="px-3 py-2 border rounded-md text-sm"
          data-testid="priority-filter"
        >
          <option value="">All Priorities</option>
          {PRIORITIES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={() => { fetchRequests(); fetchStats(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No maintenance requests</h3>
              <p className="text-muted-foreground">
                {isTenant && rentals.length > 0
                  ? "Click 'New Request' to report a maintenance issue"
                  : isProjectManager
                  ? "No maintenance requests have been submitted yet"
                  : "You don't have any active rentals to report maintenance for"}
              </p>
            </CardContent>
          </Card>
        ) : (
          requests.map((req) => {
            const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            const priorityConfig = PRIORITIES.find((p) => p.value === req.priority) || PRIORITIES[1];

            return (
              <Card key={req.request_id} className="overflow-hidden" data-testid={`request-${req.request_id}`}>
                <div className="flex flex-col md:flex-row">
                  <div className="flex-1 p-4 md:p-6">
                    <div className="flex flex-wrap items-start gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig.color}`}>
                        {priorityConfig.label}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {CATEGORIES.find((c) => c.value === req.category)?.label || req.category}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold mb-1">{req.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{req.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="truncate">{req.property_title || req.property_ref || "Property"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="truncate">{req.tenant_name || "Tenant"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{formatDate(req.created_at)}</span>
                      </div>
                      {req.estimated_cost && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <span>K {req.estimated_cost.toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {req.requires_landlord_auth && req.status === "awaiting_landlord" && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2 text-sm text-yellow-800">
                        <AlertCircle className="w-4 h-4" />
                        Awaiting landlord authorization (cost exceeds monthly rent)
                      </div>
                    )}
                  </div>

                  <div className="border-t md:border-t-0 md:border-l p-4 md:w-48 flex flex-row md:flex-col gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRequest(req)}
                      data-testid={`view-${req.request_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>

                    {isProjectManager && req.status === "pending" && (
                      <Button
                        size="sm"
                        onClick={() => setSelectedRequest({ ...req, action: "review" })}
                        data-testid={`review-${req.request_id}`}
                      >
                        Review
                      </Button>
                    )}

                    {isProjectManager && req.status === "awaiting_landlord" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setSelectedRequest({ ...req, action: "landlord" })}
                      >
                        Record Response
                      </Button>
                    )}

                    {isProjectManager && ["in_progress", "landlord_approved"].includes(req.status) && !req.linked_project_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleLinkProject(req.request_id)}
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        Create Project
                      </Button>
                    )}

                    {isProjectManager && ["in_progress", "landlord_approved"].includes(req.status) && (
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setSelectedRequest({ ...req, action: "complete" })}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                    )}

                    {isTenant && ["pending", "in_review"].includes(req.status) && req.tenant_user_id === user?.user_id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancel(req.request_id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Request Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>New Maintenance Request</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Property *</label>
                  <select
                    value={newRequest.rental_id}
                    onChange={(e) => setNewRequest({ ...newRequest, rental_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select a property</option>
                    {rentals.map((rental) => (
                      <option key={rental.rental_id} value={rental.rental_id}>
                        {rental.property_title || rental.property_ref || rental.rental_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      value={newRequest.category}
                      onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {CATEGORIES.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={newRequest.priority}
                      onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {PRIORITIES.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Issue Title *</label>
                  <Input
                    value={newRequest.title}
                    onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                    placeholder="Brief description of the issue"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <Textarea
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                    placeholder="Detailed description of the maintenance issue"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Service Date</label>
                  <Input
                    type="date"
                    value={newRequest.preferred_date}
                    onChange={(e) => setNewRequest({ ...newRequest, preferred_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Access Instructions</label>
                  <Textarea
                    value={newRequest.access_instructions}
                    onChange={(e) => setNewRequest({ ...newRequest, access_instructions: e.target.value })}
                    placeholder="How can the maintenance team access the property?"
                    rows={2}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    Submit Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View/Action Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle>{selectedRequest.title}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedRequest.property_title} ({selectedRequest.property_ref})
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(null)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status & Priority */}
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedRequest.status]?.color}`}>
                  {STATUS_CONFIG[selectedRequest.status]?.label}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${PRIORITIES.find((p) => p.value === selectedRequest.priority)?.color}`}>
                  {PRIORITIES.find((p) => p.value === selectedRequest.priority)?.label} Priority
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100">
                  {CATEGORIES.find((c) => c.value === selectedRequest.category)?.label}
                </span>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-medium mb-1">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedRequest.description}</p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Tenant:</span>
                  <p className="font-medium">{selectedRequest.tenant_name}</p>
                  {selectedRequest.tenant_email && <p className="text-xs text-muted-foreground">{selectedRequest.tenant_email}</p>}
                </div>
                <div>
                  <span className="text-muted-foreground">Landlord:</span>
                  <p className="font-medium">{selectedRequest.landlord_name || "N/A"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Monthly Rent:</span>
                  <p className="font-medium">K {selectedRequest.monthly_rent?.toLocaleString() || "N/A"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                {selectedRequest.estimated_cost && (
                  <div>
                    <span className="text-muted-foreground">Estimated Cost:</span>
                    <p className="font-medium">K {selectedRequest.estimated_cost.toLocaleString()}</p>
                  </div>
                )}
                {selectedRequest.actual_cost && (
                  <div>
                    <span className="text-muted-foreground">Actual Cost:</span>
                    <p className="font-medium">K {selectedRequest.actual_cost.toLocaleString()}</p>
                  </div>
                )}
                {selectedRequest.preferred_date && (
                  <div>
                    <span className="text-muted-foreground">Preferred Date:</span>
                    <p className="font-medium">{selectedRequest.preferred_date}</p>
                  </div>
                )}
              </div>

              {selectedRequest.access_instructions && (
                <div>
                  <h4 className="font-medium mb-1">Access Instructions</h4>
                  <p className="text-sm text-muted-foreground">{selectedRequest.access_instructions}</p>
                </div>
              )}

              {selectedRequest.resolution_notes && (
                <div>
                  <h4 className="font-medium mb-1">Resolution Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedRequest.resolution_notes}</p>
                </div>
              )}

              {/* Review Action */}
              {selectedRequest.action === "review" && isProjectManager && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Review Request</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Estimated Cost (K) *</label>
                      <Input
                        type="number"
                        value={reviewForm.estimated_cost}
                        onChange={(e) => setReviewForm({ ...reviewForm, estimated_cost: e.target.value })}
                        placeholder="Enter estimated repair cost"
                      />
                      {selectedRequest.monthly_rent > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          If cost exceeds K {selectedRequest.monthly_rent.toLocaleString()} (monthly rent), landlord authorization will be required.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Notes</label>
                      <Textarea
                        value={reviewForm.notes}
                        onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                        placeholder="Internal notes"
                        rows={2}
                      />
                    </div>
                    <Button onClick={() => handleReview(selectedRequest.request_id)} className="w-full">
                      Submit Review
                    </Button>
                  </div>
                </div>
              )}

              {/* Landlord Response Action */}
              {selectedRequest.action === "landlord" && isProjectManager && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Record Landlord Response</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Landlord Decision</label>
                      <select
                        value={landlordForm.response}
                        onChange={(e) => setLandlordForm({ ...landlordForm, response: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Landlord Notes</label>
                      <Textarea
                        value={landlordForm.notes}
                        onChange={(e) => setLandlordForm({ ...landlordForm, notes: e.target.value })}
                        placeholder="Any notes from the landlord"
                        rows={2}
                      />
                    </div>
                    <Button onClick={() => handleLandlordResponse(selectedRequest.request_id)} className="w-full">
                      Save Response
                    </Button>
                  </div>
                </div>
              )}

              {/* Complete Action */}
              {selectedRequest.action === "complete" && isProjectManager && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Complete Maintenance</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Actual Cost (K)</label>
                      <Input
                        type="number"
                        value={completeForm.actual_cost}
                        onChange={(e) => setCompleteForm({ ...completeForm, actual_cost: e.target.value })}
                        placeholder="Final cost of repairs"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Resolution Notes</label>
                      <Textarea
                        value={completeForm.resolution_notes}
                        onChange={(e) => setCompleteForm({ ...completeForm, resolution_notes: e.target.value })}
                        placeholder="What was done to resolve the issue"
                        rows={3}
                      />
                    </div>
                    <Button onClick={() => handleComplete(selectedRequest.request_id)} className="w-full bg-green-600 hover:bg-green-700">
                      Mark as Completed
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MaintenanceManagement;
